/**
 * Halven House section behaviours: review paging + rating filter, New season tabs, review rail.
 */

/** Reviews grid: shows `batch` cards (mobile: `batchMobile`), Load more, 5★/4★ filters. */
class HhReviews extends HTMLElement {
  #shown = 0;
  #filter = 'all';

  connectedCallback() {
    this.#shown = this.#batch;
    const section = this.closest('section');
    section?.querySelector('[data-more]')?.addEventListener('click', () => {
      this.#shown += this.#batch;
      this.#draw();
    });
    section?.querySelector('[data-rf]')?.addEventListener('click', (event) => {
      const b = /** @type {HTMLElement} */ (event.target).closest('button');
      if (!b) return;
      b.parentElement?.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      this.#filter = b.dataset.f ?? 'all';
      this.#shown = this.#batch;
      this.#draw();
    });
    this.#draw();
  }

  get #batch() {
    return Number((window.innerWidth < 600 ? this.dataset.batchMobile : this.dataset.batch) || 8);
  }

  #draw() {
    const cards = /** @type {HTMLElement[]} */ (Array.from(this.children));
    const match = cards.filter((c) => this.#filter === 'all' || c.dataset.r === this.#filter);
    cards.forEach((c) => (c.hidden = true));
    match.slice(0, this.#shown).forEach((c) => (c.hidden = false));
    const more = /** @type {HTMLElement | null | undefined} */ (this.closest('section')?.querySelector('[data-more]'));
    if (more) more.hidden = this.#shown >= match.length;
  }
}
if (!customElements.get('hh-reviews')) customElements.define('hh-reviews', HhReviews);

/** New season grid: product-type tabs filter the cards without a reload, `limit` cards per tab. */
class HhTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const b = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
      if (!(b instanceof HTMLElement)) return;
      this.querySelectorAll('[data-tab]').forEach((x) => x.classList.toggle('on', x === b));
      this.#draw(b.dataset.tab ?? '');
    });
    this.#draw('');
  }

  /** @param {string} type */
  #draw(type) {
    const limit = Number(this.dataset.limit || 8);
    let n = 0;
    for (const card of /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-grid] > .card')))) {
      const ok = (!type || card.dataset.type === type) && n < limit;
      card.hidden = !ok;
      if (ok) n++;
    }
  }
}
if (!customElements.get('hh-tabs')) customElements.define('hh-tabs', HhTabs);

/**
 * Horizontal rail (editorial reviews): prev/next buttons in the owning section scroll the
 * `[data-track]` by one viewport of items; `[data-bar]` tracks the scroll position.
 */
class HhRail extends HTMLElement {
  connectedCallback() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;
    this.track = track;
    const section = this.closest('section');
    this.prev = /** @type {HTMLButtonElement | null | undefined} */ (section?.querySelector('[data-prev]'));
    this.next = /** @type {HTMLButtonElement | null | undefined} */ (section?.querySelector('[data-next]'));
    this.bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-bar]'));
    this.prev?.addEventListener('click', () => this.#go(-1));
    this.next?.addEventListener('click', () => this.#go(1));
    track.addEventListener('scroll', () => this.#update(), { passive: true });
    window.addEventListener('resize', () => this.#update(), { passive: true });
    this.#update();
  }

  /** @param {number} dir */
  #go(dir) {
    const t = /** @type {HTMLElement} */ (this.track);
    const item = /** @type {HTMLElement | null} */ (t.children[1] ?? t.children[0]);
    const step = item ? item.offsetWidth : t.clientWidth;
    const n = Math.max(1, Math.floor(t.clientWidth / step) - 1);
    t.scrollBy({ left: dir * step * n, behavior: 'smooth' });
  }

  #update() {
    const t = /** @type {HTMLElement} */ (this.track);
    const max = t.scrollWidth - t.clientWidth;
    const ratio = max > 0 ? t.scrollLeft / max : 0;
    const visible = t.scrollWidth > 0 ? Math.min(1, t.clientWidth / t.scrollWidth) : 1;
    if (this.bar) {
      this.bar.style.width = `${Math.max(8, visible * 100)}%`;
      this.bar.style.transform = `translateX(${ratio * (1 / Math.max(visible, 0.08) - 1) * 100}%)`;
    }
    if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
    if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
  }
}
if (!customElements.get('hh-rail')) customElements.define('hh-rail', HhRail);
